// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — VersionTimeline (M3: V·IANet Flow)
// Slide-in panel showing version history for a flowchart
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { FLOW_TOKENS as T } from '../shared/flowTokens';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { request } from '../../../../shared/services/api';

// ─── Types ──────────────────────────────────────────────────────────────────

interface VersionEntry {
  id: string;
  version: number;
  estado: string;
  changelog?: string;
  creadoPor: string;
  fechaCreacion: string;
  fechaAprobacion?: string;
  fechaPublicacion?: string;
  firmado?: boolean;
  fechaFirma?: string;
}

interface VersionTimelineProps {
  procesoId: string;
  currentVersion?: number;
  onSelectVersion: (version: string) => void;
  onCompare?: (v1: number, v2: number) => void;
  onClose: () => void;
}

// ─── Status helpers ─────────────────────────────────────────────────────────

function estadoLabel(estado: string): string {
  switch (estado) {
    case 'PUBLICADO':
      return 'Vigente';
    case 'APROBADO':
      return 'Aprobado';
    case 'EN_REVISION':
      return 'En revision';
    case 'ARCHIVADO':
      return 'Archivado';
    default:
      return 'Borrador';
  }
}

function estadoColor(estado: string): string {
  switch (estado) {
    case 'PUBLICADO':
      return T.color.statusApproved;
    case 'APROBADO':
      return T.color.statusApproved;
    case 'EN_REVISION':
      return T.color.statusPending;
    case 'ARCHIVADO':
      return T.color.textTertiary;
    default:
      return T.color.statusDraft;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function VersionTimeline({
  procesoId,
  currentVersion,
  onSelectVersion,
  onCompare,
  onClose,
}: VersionTimelineProps) {
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);

  useEffect(() => {
    if (!procesoId) return;
    setLoading(true);
    setError(null);
    request<{ data?: VersionEntry[] } | VersionEntry[]>(`/flujogramas/${procesoId}/versions`)
      .then((body) => {
        const list = Array.isArray(body) ? body : (body.data ?? []);
        setVersions(list);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Error al cargar versiones'),
      )
      .finally(() => setLoading(false));
  }, [procesoId]);

  return (
    <div
      style={{
        width: 200,
        backgroundColor: T.color.surface,
        borderLeft: `1px solid ${T.color.border}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 10px',
          borderBottom: `1px solid ${T.color.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h3
          style={{
            fontSize: '8px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: T.color.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.7px',
            margin: 0,
          }}
        >
          VERSIONES
        </h3>
        <div style={{ display: 'flex', gap: 2 }}>
          {onCompare && versions.length >= 2 && (
            <button
              onClick={() => {
                if (compareMode && selectedForCompare.length === 2) {
                  onCompare(Math.min(...selectedForCompare), Math.max(...selectedForCompare));
                  setCompareMode(false);
                  setSelectedForCompare([]);
                } else {
                  setCompareMode(!compareMode);
                  setSelectedForCompare([]);
                }
              }}
              title={
                compareMode
                  ? selectedForCompare.length === 2
                    ? 'Comparar seleccionadas'
                    : 'Seleccione 2 versiones'
                  : 'Comparar'
              }
              style={{
                padding: '1px 5px',
                border: `1px solid ${compareMode ? T.color.secondary : T.color.border}`,
                background: compareMode ? '#F0FBFF' : 'none',
                borderRadius: 3,
                cursor: 'pointer',
                fontSize: '7px',
                fontWeight: 600,
                fontFamily: T.font.family,
                color: compareMode ? T.color.secondary : T.color.textTertiary,
              }}
            >
              {compareMode
                ? selectedForCompare.length === 2
                  ? 'Comparar'
                  : `${selectedForCompare.length}/2`
                : 'Diff'}
            </button>
          )}
          <button
            onClick={onClose}
            title="Cerrar"
            style={{
              width: 18,
              height: 18,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 12,
              color: T.color.textTertiary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 3,
            }}
          >
            x
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {loading && <LoadingSpinner text="Cargando versiones..." size="sm" />}
        {error && (
          <p style={{ fontSize: '8px', fontFamily: T.font.family, color: T.color.statusRejected }}>
            {error}
          </p>
        )}
        {!loading && !error && versions.length === 0 && (
          <p style={{ fontSize: '8px', fontFamily: T.font.family, color: T.color.textTertiary }}>
            Sin versiones registradas.
          </p>
        )}

        {/* Timeline */}
        {versions.map((v, idx) => {
          const isActive = currentVersion === v.version;
          const isVigente = v.estado === 'PUBLICADO';
          const isLast = idx === versions.length - 1;
          const isSelectedForCompare = compareMode && selectedForCompare.includes(v.version);

          return (
            <div
              key={v.id}
              onClick={() => {
                if (compareMode) {
                  setSelectedForCompare((prev) => {
                    if (prev.includes(v.version)) return prev.filter((x) => x !== v.version);
                    if (prev.length >= 2) return [prev[1], v.version];
                    return [...prev, v.version];
                  });
                } else {
                  onSelectVersion(String(v.version));
                }
              }}
              style={{
                position: 'relative',
                paddingLeft: 14,
                paddingBottom: isLast ? 0 : 10,
                cursor: 'pointer',
                borderLeft: `2px solid ${isVigente ? T.color.navy : T.color.border}`,
                marginLeft: 4,
              }}
            >
              {/* Dot */}
              <div
                style={{
                  position: 'absolute',
                  left: -5,
                  top: 1,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: isSelectedForCompare
                    ? T.color.secondary
                    : isActive
                      ? T.color.secondary
                      : isVigente
                        ? T.color.navy
                        : T.color.border,
                  border: `1.5px solid ${isSelectedForCompare ? T.color.secondary : isActive ? T.color.secondary : isVigente ? T.color.navy : T.color.borderStrong}`,
                  boxShadow: isSelectedForCompare ? `0 0 0 2px rgba(51,188,225,0.3)` : undefined,
                }}
              />

              {/* Version label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                <span
                  style={{
                    fontSize: '8.5px',
                    fontWeight: 700,
                    fontFamily: T.font.family,
                    color: isActive ? T.color.secondary : T.color.textPrimary,
                  }}
                >
                  Ed. {String(v.version).padStart(2, '0')}
                </span>
                {isVigente && (
                  <span
                    style={{
                      fontSize: '6.5px',
                      fontWeight: 600,
                      fontFamily: T.font.family,
                      color: T.color.statusApproved,
                      padding: '0 4px',
                      borderRadius: 2,
                      border: `1px solid ${T.color.statusApproved}`,
                      textTransform: 'uppercase',
                      lineHeight: 1.4,
                    }}
                  >
                    VIGENTE
                  </span>
                )}
                {v.firmado && (
                  <span
                    title={
                      v.fechaFirma
                        ? `Firmado el ${new Date(v.fechaFirma).toLocaleDateString()}`
                        : 'Publicado con firma electrónica'
                    }
                    style={{
                      fontSize: '6.5px',
                      fontWeight: 600,
                      fontFamily: T.font.family,
                      color: T.color.secondary,
                      padding: '0 4px',
                      borderRadius: 2,
                      border: `1px solid ${T.color.secondary}`,
                      textTransform: 'uppercase',
                      lineHeight: 1.4,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <svg
                      width="7"
                      height="7"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                    FIRMADO
                  </span>
                )}
              </div>

              {/* Status */}
              <div
                style={{
                  fontSize: '7.5px',
                  fontFamily: T.font.family,
                  color: estadoColor(v.estado),
                  fontWeight: 600,
                  marginBottom: 1,
                }}
              >
                {estadoLabel(v.estado)}
              </div>

              {/* Date + author */}
              <div
                style={{
                  fontSize: '7px',
                  fontFamily: T.font.family,
                  color: T.color.textTertiary,
                  marginBottom: 1,
                }}
              >
                {new Date(v.fechaCreacion).toLocaleDateString()} — {v.creadoPor}
              </div>

              {/* Changelog */}
              {v.changelog && (
                <div
                  style={{
                    fontSize: '7px',
                    fontFamily: T.font.family,
                    color: T.color.textTertiary,
                    fontStyle: 'italic',
                    lineHeight: 1.3,
                  }}
                >
                  {v.changelog}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
